import API from './config'

const authConfig = () => ({
    headers: {
        'x-access-token': localStorage.getItem('token'),
    }
});

//Formato e tamanho dos documentos complementares. O backend repete a checagem no signedUploadUrl
//(TRADEMATE_COMPLEMENTARY_* em contants.js); aqui e para barrar antes de qualquer upload.
const COMPLEMENTARY_MAX_BYTES = 5 * 1024 * 1024;
const COMPLEMENTARY_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

/**
 * @returns {string|null} o motivo de o arquivo nao poder subir, ou null se pode
 */
function complementaryFileError(file) {

    if (COMPLEMENTARY_CONTENT_TYPES.indexOf(file.type) === -1) {
        return file.name + ': formato não permitido (use PDF, JPG ou PNG)';
    }
    if (file.size > COMPLEMENTARY_MAX_BYTES) {
        return file.name + ': arquivo maior que 5 MB';
    }

    return null;
}

/**
 * Cadastro Trademate (Administrativo > Credenciamento > Cadastro Trademate).
 *
 * Segue o envelope { success, body } usado nos demais modulos de API. Nas rotas que validam dados
 * o backend devolve `errors: string[]` no corpo do 400 - a tela mostra essa lista direto.
 */
export const TrademateAPI = {

    async getByClientId(clientId) {

        try {

            const response = await API.get(`/trademate/getByClientId?client_id=${clientId}`, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    async getAvailableManagers(clientId) {

        try {

            const response = await API.get(`/trademate/availableManagers?client_id=${clientId}`, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    /**
     * Salva a etapa 1 inteira. As listas vao completas: o backend regrava tudo.
     *
     * @param {{
     *  client_id: number,
     *  custodians: {name: string, cnpj: string, is_main: boolean}[],
     *  settlements: {name: string, cnpj: string, is_main: boolean}[],
     *  manager_user_ids: number[]
     * }} data
     */
    async saveData(data) {

        try {

            const response = await API.put('/trademate/saveData', data, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    async generateDocument(documentId) {

        try {

            const response = await API.post('/trademate/generateDocument',
                { document_id: documentId }, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    /**
     * Sobe o assinado em duas etapas: pede a URL assinada (a chave e montada no servidor) e faz o
     * PUT direto no S3, como uploadMultipleFiles ja faz no modulo server_files.
     */
    async uploadSignedDocument(documentId, file) {

        const MAX_FILE_SIZE = 20 * 1024 * 1024;

        if (file.size > MAX_FILE_SIZE) {
            return { success: false, body: { message: 'O arquivo excede o tamanho maximo de 20MB.' } };
        }

        try {

            const responseUrl = await API.post('/trademate/signedUploadUrl', {
                document_id: documentId,
                file_name: file.name,
                content_type: file.type,
                file_size: file.size,
            }, authConfig());

            const { url, key, file_name } = responseUrl.data;

            await API.put(url, file, {
                headers: { 'Content-Type': file.type || 'application/pdf' },
                maxBodyLength: Infinity,
            });

            const responseAttach = await API.post('/trademate/attachSigned', {
                document_id: documentId,
                key,
                file_name,
            }, authConfig());

            return { success: true, body: responseAttach.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    /**
     * Sobe os arquivos de um documento complementar, um a um, pelo mesmo caminho do assinado.
     *
     * Valida todos antes de enviar o primeiro: numa selecao de frente e verso com um arquivo
     * invalido, nada sobe - senao a linha ficaria meio anexada sem o usuario perceber.
     *
     * @param {File[]} files
     */
    async uploadComplementaryFiles(documentId, files) {

        const errors = files.map(complementaryFileError).filter(error => error !== null);

        if (errors.length > 0) {
            return { success: false, body: { message: errors.join(' • ') } };
        }

        let response = null;

        for (const file of files) {
            response = await TrademateAPI.uploadSignedDocument(documentId, file);
            if (!response.success) return response;
        }

        return response;
    },

    async deleteDocumentFile(fileId) {

        try {

            const response = await API.delete(`/trademate/documentFile/${fileId}`, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    async deleteSignedDocument(documentId) {

        try {

            const response = await API.delete(`/trademate/signedDocument/${documentId}`, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    /**
     * @param {'generated'|'signed'|'file'} type `file` e um arquivo de documento complementar
     * @param {number} [fileId] obrigatorio quando type = 'file'
     */
    async getDocumentUrl(documentId, type, fileId) {

        try {

            const fileParam = fileId ? `&file_id=${fileId}` : '';

            const response = await API.get(
                `/trademate/documentUrl?document_id=${documentId}&type=${type}${fileParam}`,
                authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    /**
     * O que falta para poder enviar. O backend é a autoridade: a etapa 3 só exibe.
     */
    async getPendencies(clientId) {

        try {

            const response = await API.get(`/trademate/pendencies?client_id=${clientId}`, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    async send(clientId) {

        try {

            const response = await API.post('/trademate/send', { client_id: clientId }, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    /** Redispara o e-mail da LEMA de um cadastro já enviado; devolve os destinatários em `body`. */
    async resendEmail(clientId) {

        try {

            const response = await API.post('/trademate/resendEmail',
                { client_id: clientId }, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

    async reopen(clientId, reopenReason) {

        try {

            const response = await API.put('/trademate/reopen',
                { client_id: clientId, reopen_reason: reopenReason }, authConfig());

            return { success: true, body: response.data };

        } catch (error) {

            return {
                success: false,
                body: error.response ? error.response.data : 'NO_DATA_ERROR'
            };
        }
    },

};
